import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from "react"
import _ from "lodash"

import "./Quiz.scss"
import { QuizActions, WithQuiz } from "./context"
import { ILayoutContext, WithLayout } from "./layout"
import {
  isRankingRelevant
} from "./helper"
import {
  Introduction,
  ReleasedAt,
  Busy
} from "./Ui"
import QuizCard from "./Ui/QuizCard"
import Cover from "./Cover"
import Question from "./Question"
import Result from "./Result"

import { useQuiz } from "./context"
import { useApi } from "@api"

import { Teaser, TeasersWrapper, useTeasers, WithTeasers } from "./Teaser"
import { Ranking, useRanking, WithRanking } from "./Ranking"
import ShareLinks from "./Ui/ShareLinks"
import PointsToResult from "./Ui/PersonalityTestPointsToResult"
import QuizEmbedInfoContext from "./QuizEmbedInfoContext"
import SetQuizLayoutClasses from "./SetQuizLayoutClasses"

export const QuizRaw: React.FC = () => {
  const {
    displayOutsideIntroduction,
    displayOutsidePersonalityTestPointsToResultInfo,
    displayOutsideRanking,
    displayOutsideSharing,
    displayOutsideTeasers,
  } = useContext(QuizEmbedInfoContext)
  const {
    quiz,
    busy,
  } = useQuiz()
  const {
    teasers
  } = useTeasers()
  const {
    ranking,
    toPage: toRankingPage
  } = useRanking()

  return (
    <SetQuizLayoutClasses>
      <QuizCard Overlay={busy && <Busy />}>
        {quiz?.cover && <Cover />}
        {quiz?.question && <Question key={quiz.meta.question_results.length} />}
        {quiz?.result && <Result key={`${quiz.meta.question_results.length}-${quiz.result.normal_quiz ? quiz.result.normal_quiz.grade : "perso"}`} />}
      </QuizCard>
      {quiz?.meta.released_at &&
        <ReleasedAt date={quiz.meta.released_at} />}
      {displayOutsideSharing && quiz?.result &&
        <ShareLinks share={quiz.result.share} />}
      {displayOutsidePersonalityTestPointsToResultInfo && !!quiz?.result?.personality_test_points &&
        <PointsToResult points_to_result={quiz.result.personality_test_points.points_to_result} />}
      {displayOutsideRanking && !!quiz?.result?.normal_quiz && ranking?.quiz &&
        <Ranking ranking={ranking.quiz} toPage={toRankingPage} />}
      {displayOutsideIntroduction && quiz?.meta.introduction &&
        <Introduction text={quiz.meta.introduction} />}
      {displayOutsideTeasers && teasers && teasers.length > 0 &&
        <TeasersWrapper>
          {_.map(teasers, (t, index) => <Teaser key={index} teaser={t} />)}
        </TeasersWrapper>}
    </SetQuizLayoutClasses>
  )
}

interface QuizCompState {
  quiz?: Quiz.Api.Quiz
  busy: boolean
  error?: Base.Api.Error
  mounted: boolean,
  palletteId: string,
}

const isAndroidWebView = () => {
  const ua = navigator.userAgent.toLowerCase()
  return ua.indexOf("android") > -1 && (ua.indexOf("wv") > -1 || ua.indexOf("webview") > -1)
}

const useReloadPageAfterTwoQuestions = (quiz?: Quiz.Api.Quiz, options?: { disable?: boolean }) => {
  const [ startedWithQuestions, setStartedWithQuestions ] = useState(-1)
  const numQuestionResults = quiz?.meta.question_results.length || 0

  const shouldReload = (options?.disable || isAndroidWebView())
    ? false
    : startedWithQuestions > -1 && (numQuestionResults >= startedWithQuestions + 2)

  useEffect(() => { // when quiz first exists: store current num-questions-answered
    if (quiz && startedWithQuestions === -1) { setStartedWithQuestions(quiz.meta.question_results.length)}
  }, [quiz, startedWithQuestions])

  useEffect(() => {
    if (shouldReload) {
      window.location.reload()
    }
  }, [shouldReload])

  return { isReloading: shouldReload }
}

interface QuizCompProps {
  quizUid: string
  isEmbed?: boolean
  forceMode?: ILayoutContext["mode"]
}

const QuizInsideWrapThatCanRemount: React.FC<QuizCompProps & { forceRemount(): void }> = ({ quizUid, isEmbed, forceMode, forceRemount }) => {
  const { colorPalettes, host } = useContext(QuizEmbedInfoContext)
  const forceRemountRef = useRef(forceRemount)
  forceRemountRef.current = forceRemount
  const [ state, setState ] = useState<QuizCompState>({
    busy: false,
    mounted: true,
    palletteId: "",
  })
  const [ teasers, setTeasers ] = useState<Teaser.Api.Teaser[]>([])
  const [ ranking, setRanking ] = useState<Ranking.Api.GetRankingResp>({})
  const stateRef = useRef(state)
  stateRef.current = state
  const isMountedRef = useRef(true)

  const quizUidRef = useRef(quizUid)
  quizUidRef.current = quizUid

  const isEmbedRef = useRef(isEmbed)
  isEmbedRef.current = isEmbed

  useEffect(() => { // store if mounted

    return () => { // cleanup
      isMountedRef.current = false
    }
  }, [])

  const { isReloading } = useReloadPageAfterTwoQuestions(
    state.quiz,
    {
      disable: isEmbed // don't auto-reload when embed!
    })
  const isReloadingRef = useRef(isReloading)
  isReloadingRef.current = isReloading

  const api = useApi()
  const apiRef = useRef(api)
  apiRef.current = api

  const whereAmI = state.quiz
    ? (
        state.quiz.cover
          ? "cover"
          : state.quiz.result
            ? "result"
            : state.quiz.meta.question_results.length + 1
      )
    : "nowhere"

  useEffect(() => { // new Pallette for each question
    if (!isReloadingRef.current ) {
      const source = whereAmI === "cover"
        ? colorPalettes.cover
        : whereAmI === "result"
          ? colorPalettes.result
          : whereAmI === "nowhere"
            ? ["0"]
            : colorPalettes.question

      setState({
        ...stateRef.current,
        palletteId: source[_.random(0, source.length - 1)]!
      })
    }
  }, [whereAmI, colorPalettes])


  const __handleResponse = useCallback((resp: {data?: {quiz: Quiz.Api.Quiz}, error?: Base.Api.Error}) => {
    if (!stateRef.current.mounted) { return }

    const newState = {
      ...stateRef.current,
      busy: false, // -> unbusy
      error: resp.error
    }

    if (resp.data?.quiz) { newState.quiz = resp.data.quiz } // only override quiz if new one given (so even when error, the old state is still viewable)

    setState(newState)
  }, [])

  const __handleResponseRef = useRef(__handleResponse)
  __handleResponseRef.current = __handleResponse

  useEffect(() => { // ----------------- Load Quiz -----------------
    let mounted = true
    setState((prev) => ({ ...prev, busy: true })) // busy...

    apiRef.current.send<Quiz.Api.GetQuizReq, Quiz.Api.GetQuizResp>("quiz/get_quiz", { uid: quizUid })
      .then(__handleResponseRef.current)

    if (!isEmbedRef.current) { // unless embed: fetch teasers (content below quiz)
      apiRef.current.send<Teaser.Api.GetTeasersReq, Teaser.Api.GetTeasersResp>("quiz/get_teasers", { uid: quizUid })
        .then((resp) => {
          if (resp.data && mounted) {
            setTeasers(resp.data.teasers)
          }
        })
    }

    return () => { // cleanup
      mounted = false
      setState((prev) => ({ ...prev, mounted: false }))
    }
  }, [quizUid])

  const shallRequestRanking = !!state.quiz?.result?.normal_quiz && isRankingRelevant(state.quiz)
  const ownStateInRanking = _.isNumber(state.quiz?.result?.normal_quiz?.own_rank)
    ? "isEnteredInRanking"
    : "notEnteredInRanking"

  const toRankingPage = useCallback((page?: number) => {
    apiRef.current.send<Ranking.Api.GetRankingReq, Ranking.Api.GetRankingResp>("quiz/get_ranking", _.isNumber(page) ? { uid: quizUidRef.current, page } : { uid: quizUidRef.current })
      .then((resp) => {
        if (resp.data && isMountedRef.current) {
          setRanking(resp.data)
        }
      })
  }, [])
  const toRankingPageRef = useRef(toRankingPage)
  toRankingPageRef.current = toRankingPage

  useEffect(() => { // load ranking when showing quiz-result (and NO personality test) or when own-state-in-ranking changed
    if (shallRequestRanking) {
      toRankingPageRef.current()
    }

    return undefined // no cleanup
  }, [shallRequestRanking, ownStateInRanking])

  const answerQuestion = useCallback((submission?: Quiz.Api.AnswerQuestionReq["submission"], _wasTimeout?: boolean) => {
    setState((prev) => ({ ...prev, busy: true }))

    const deprecatedInfo: { picked_option?: number, picked_options?: number[] } = submission
      ? (
          submission.type === "multiple_choice"
          ? { picked_option: submission.picked_option, picked_options: [submission.picked_option] }
          : submission.type === "slider"
            ? { picked_option: submission.picked_option, picked_options: [submission.picked_option] }
            : submission.type === "multi_select"
              ? { picked_option: submission.picked_options?.[0], picked_options: submission.picked_options }
              : {}
        )
      : {}

    const payload = submission
      ? {
          ...deprecatedInfo,
          submission,
        }
      : { was_timeout: true }

    apiRef.current.send<Quiz.Api.AnswerQuestionReq, Quiz.Api.AnswerQuestionResp>("quiz/answer_question", {
      uid: quizUidRef.current,
      ...payload
    }).then(__handleResponseRef.current)
  }, [])

  const startQuiz = useCallback(() => {
    setState((prev) => ({ ...prev, busy: true }))

    apiRef.current.send<Quiz.Api.StartQuizReq, Quiz.Api.StartQuizResp>("quiz/start", { uid: quizUidRef.current })
      .then(__handleResponseRef.current)
  }, [])

  const startQuestionCountdown = useCallback(() => {
    setState((prev) => ({ ...prev, busy: true }))

    apiRef.current.send<Quiz.Api.StartQuestionCountdownReq, Quiz.Api.StartQuestionCountdownResp>("quiz/start_question_countdown", { uid: quizUidRef.current })
      .then(__handleResponseRef.current)
  }, [])

  const cancelQuiz = useCallback(() => {
    setState((prev) => ({ ...prev, busy: true }))

    apiRef.current.send<Quiz.Api.StartQuizReq, Quiz.Api.StartQuizResp>("quiz/cancel", { uid: quizUidRef.current })
      .then(() => forceRemountRef.current())
  }, [])

  const continueQuiz = useCallback(() => {
    setState((prev) => ({ ...prev, busy: true }))

    apiRef.current.send<Quiz.Api.ContinueQuizReq, Quiz.Api.ContinueQuizResp>("quiz/continue", { uid: quizUidRef.current })
      .then(__handleResponseRef.current)
  }, [])

  const enterRanking = useCallback((userToken: string) => {
    setState((prev) => ({ ...prev, busy: true }))

    apiRef.current.send<Quiz.Api.EnterRankingReq, Quiz.Api.EnterRankingResp>("quiz/enter_ranking", { uid: quizUidRef.current, user_token: userToken })
      .then(__handleResponseRef.current)
  }, [])

  const actions = useMemo<QuizActions>(() => ({
    startQuiz,
    startQuestionCountdown,
    answerQuestion,
    cancelQuiz,
    continueQuiz,
    enterRanking,
  }), [
    startQuiz,
    startQuestionCountdown,
    answerQuestion,
    cancelQuiz,
    continueQuiz,
    enterRanking,
  ])

  return (
    <WithLayout embedHost={host} palletteId={state.palletteId} overrideColorTheme={state.quiz?.color_theme} forceMode={forceMode}>
      <div className="neo__quizHolder">
        {state.error && <div className="neo__quizApiError">{state.error.error_description || state.error.error}</div>}
        <WithRanking ranking={ranking} toPage={toRankingPage}>
          <WithTeasers teasers={teasers}>
            <WithQuiz quiz={isReloading ? undefined : state.quiz} busy={state.busy} actions={actions}>
              <QuizRaw />
            </WithQuiz>
          </WithTeasers>
        </WithRanking>
      </div>
    </WithLayout>
  )
}

const Quiz: React.FC<QuizCompProps> = (props) => {
  const [ forceRemountKey, setForceRemountKey ] = useState(0)

  const forceRemount = useCallback(() => setForceRemountKey((prev) => prev + 1), [])

  return <QuizInsideWrapThatCanRemount key={`${props.quizUid}--${forceRemountKey}`} {...props} forceRemount={forceRemount} />
}

export default Quiz
